/**
 * 📣 COMUNIDAD vía REST público (`/api/community/*`) — guest-first.
 *
 * El feed de descubrimiento es dato público [H]. El invitado (sin token de sync)
 * lo ojea por estos endpoints; el usuario autenticado usa la vista local-first de
 * PowerSync (offline + reactiva). Respuestas camelCase (serde), como el catálogo.
 */

import { SERVER_BASE_URL } from "@/lib/api";

interface CommunityPostDTO {
  id: string;
  userId: string;
  authorName?: string | null;
  authorImage?: string | null;
  caption?: string | null;
  productId?: string | null;
  productName?: string | null;
  media: string[];
  rating?: number | null;
  likeCount: number;
  commentCount: number;
  createdAt: string;
}

interface CommunityCommentDTO {
  id: string;
  postId: string;
  authorName?: string | null;
  authorImage?: string | null;
  body: string;
  createdAt: string;
}

function communityUrl(path: string): string {
  return `${SERVER_BASE_URL.replace(/\/$/, "")}/api/community${path}`;
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(communityUrl(path), { credentials: "include", signal });
  if (!res.ok) {
    throw new Error(`community REST ${path} → HTTP ${res.status}`);
  }
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return res.json() as Promise<T>;
}

/** Feed cronológico público. */
export async function fetchFeed(signal?: AbortSignal): Promise<CommunityPostDTO[]> {
  return getJson<CommunityPostDTO[]>(`/feed`, signal);
}

/** Detalle de una publicación. Devuelve `null` si no existe (404). */
export async function fetchPost(
  id: string,
  signal?: AbortSignal,
): Promise<CommunityPostDTO | null> {
  try {
    return await getJson<CommunityPostDTO>(`/post/${encodeURIComponent(id)}`, signal);
  } catch {
    return null;
  }
}

/** Comentarios de una publicación. */
export async function fetchComments(
  id: string,
  signal?: AbortSignal,
): Promise<CommunityCommentDTO[]> {
  return getJson<CommunityCommentDTO[]>(`/post/${encodeURIComponent(id)}/comments`, signal);
}

/** Galería comunitaria de un producto (publicaciones etiquetadas). */
export async function fetchProductGallery(
  productId: string,
  signal?: AbortSignal,
): Promise<CommunityPostDTO[]> {
  return getJson<CommunityPostDTO[]>(`/product/${encodeURIComponent(productId)}/gallery`, signal);
}
